import React, { useEffect, useRef, useState } from 'react'
import { useQueryClient, useMutation } from 'react-query'
import * as yup from 'yup'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import Box from '@mui/material/Box'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import { InputAdornment, MenuItem } from '@mui/material'
import { postNewCategory, putUpdateCategory } from 'services/api/modules/claim/category'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxButton from 'components/custom/AdxButton'

const ALLOCATION_CYCLE_OPTIONS = [
  { value: 1, label: 'Annually' },
  { value: 2, label: 'Monthly' },
]

const getCyclePeriodLabel = (value) =>
  ALLOCATION_CYCLE_OPTIONS.find((option) => option.value === Number(value))?.label?.toLowerCase() ||
  'annually'

const CategoryFormModal = ({
  categoryId,
  type,
  newCategory,
  categoryName,
  mileageRate,
  allocation,
  cyclePeriod,
  children,
}) => {
  const formikRef = useRef()
  const [isModalOpen, setModalOpen] = useState(false)
  const handleOpen = () => {
    setModalOpen(true)
  }
  const handleClose = () => {
    setModalOpen(false)
  }

  // form elements
  const validationSchema =
    type === 'Mileage'
      ? yup.object({
          name: yup.string().required('Category name is required'),
          rate: yup
            .number()
            .positive('Invalid amount')
            .min(0.01, 'Invalid amount')
            .required('Rate is required'),
          allocation: yup
            .number()
            .nullable()
            .when('requireAllocation', {
              is: (val) => val === true,
              then: (schema) => schema.required('Required').moreThan(1, 'Invalid amount'),
              otherwise: (schema) => schema.optional(),
            }),
          cycle_period: yup.number().oneOf([1, 2]).required('Required'),
        })
      : yup.object({
          name: yup.string().required('Category name is required'),
          allocation: yup
            .number()
            .nullable()
            .when('requireAllocation', {
              is: (val) => val === true,
              then: (schema) => schema.required('Required').moreThan(1, 'Invalid amount'),
              otherwise: (schema) => schema.optional(),
            }),
          cycle_period: yup.number().oneOf([1, 2]).required('Required'),
        })

  // create API function

  const queryClient = useQueryClient()
  const { isLoading, isSuccess, mutate } = useMutation(
    (data) => (categoryId ? putUpdateCategory(categoryId, data) : postNewCategory(data)),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('categoryData')
        await queryClient.invalidateQueries('adminCategoryData')
      },
      onError: async (error) => {
        if (error?.status === 422) {
          formikRef?.current?.setErrors({
            name: error?.data?.errors?.name ? error?.data?.errors?.name[0] : null,
            allocation: error?.data?.errors?.allocation ? error?.data?.errors?.allocation[0] : null,
            cycle_period: error?.data?.errors?.cycle_period
              ? error?.data?.errors?.cycle_period[0]
              : null,
          })
        }
      },
    }
  )

  useEffect(() => {
    isSuccess && handleClose()
  }, [isSuccess])

  return (
    <>
      <Box onClick={handleOpen}>{children}</Box>

      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          name: newCategory ? '' : categoryName,
          rate:
            type === 'Mileage' && type !== 'General'
              ? newCategory
                ? 0
                : parseFloat(mileageRate).toFixed(2)
              : null,
          claim_type: type === 'General' ? 1 : 2,
          requireAllocation: newCategory ? false : !!allocation,
          allocation: newCategory ? null : allocation,
          cycle_period: newCategory ? 1 : Number(cyclePeriod || 1),
          active: 1,
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          mutate(values, {
            onSettled: () => {
              setSubmitting(false)
            },
          })
        }}
      >
        {({ values, handleChange, handleBlur, setFieldValue, errors, touched }) => {
          return (
            <Form>
              <AdxFormModal
                open={isModalOpen}
                handleClose={handleClose}
                title={newCategory ? `New ${type} Claim` : categoryName}
                onClose={(_, reason) => {
                  if (reason !== 'backdropClick') {
                    handleClose()
                  }
                }}
                childPadding={false}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 2 }}>
                    <Field
                      name="name"
                      label="Claim name"
                      as={AdxInput}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      charLimit={50}
                      sx={{
                        width: 1,
                      }}
                      errorMessage={<ErrorMessage name="name" component={AdxErrorMessage} />}
                      isErrorShown={errors.name && touched.name}
                    />
                    {mileageRate || type === 'Mileage' ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'start',
                          gap: 1,
                        }}
                      >
                        <Field
                          name="rate"
                          label="Claim rate (RM/km)"
                          type="number"
                          as={AdxInput}
                          onChange={handleChange}
                          inputProps={{
                            min: '0.00',
                            step: '0.01',
                          }}
                          onBlur={(e) => {
                            handleBlur(e)
                            if (e.target.value === '') {
                              setFieldValue('rate', 0.0)
                            } else {
                              setFieldValue('rate', parseFloat(e.target.value).toFixed(2))
                            }
                          }}
                          startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                          endAdornment={
                            <InputAdornment position="end">per kilometre</InputAdornment>
                          }
                          errorMessage={<ErrorMessage name="rate" component={AdxErrorMessage} />}
                          isErrorShown={errors.rate && touched.rate}
                        />
                      </Box>
                    ) : null}
                    <AdxFormControlLabel
                      type="no-fill"
                      control={
                        <Field
                          name="requireAllocation"
                          disabled={!newCategory}
                          checked={values.requireAllocation}
                          onChange={(e) => {
                            setFieldValue('requireAllocation', e.target.checked)
                            if (!e.target.checked) {
                              setFieldValue('allocation', null)
                              setFieldValue('cycle_period', 1)
                            }
                          }}
                          as={AdxCheckbox}
                        />
                      }
                      label="Provide claim allocation"
                      description="Employees cannot apply for claims once allocation is used up."
                    />
                    {values.requireAllocation && (
                      <Box display="flex">
                        <Box
                          height={132}
                          paddingLeft={3}
                          marginTop={1}
                          marginLeft={5}
                          borderLeft="2px solid"
                          sx={{ borderColor: 'gray-line.main' }}
                        />
                        <Box width={1} display="flex" flexDirection="column" rowGap={2}>
                          <Field
                            name="allocation"
                            label="Allocation"
                            disabled={!newCategory}
                            as={AdxInput}
                            errorMessage={
                              <ErrorMessage name="allocation" component={AdxErrorMessage} />
                            }
                            isErrorShown={errors.allocation && touched.allocation}
                            onChange={handleChange}
                            onBlur={(e) => {
                              handleBlur(e)
                              if (e.target.value === '' || e.target.value === 0) {
                                setFieldValue('allocation', null)
                              } else {
                                setFieldValue('allocation', parseFloat(e.target.value).toFixed(2))
                              }
                            }}
                            type="number"
                            startAdornment={<InputAdornment position="start">RM</InputAdornment>}
                            endAdornment={
                              <InputAdornment position="end">
                                {getCyclePeriodLabel(values.cycle_period)}
                              </InputAdornment>
                            }
                          />
                          <AdxDropdown
                            label="Cycle"
                            name="cycle_period"
                            value={values.cycle_period}
                            sx={{ width: { xs: 1, sm: 180 } }}
                            errorMessage={
                              <ErrorMessage name="cycle_period" component={AdxErrorMessage} />
                            }
                            isErrorShown={errors.cycle_period && touched.cycle_period}
                            onChange={(e) => setFieldValue('cycle_period', Number(e.target.value))}
                            onBlur={handleBlur}
                          >
                            {ALLOCATION_CYCLE_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </AdxDropdown>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column-reverse', md: 'row' },
                      alignItems: 'center',
                      justifyContent: { xs: 'center', md: 'end' },
                      gap: 1,
                      borderTop: 1,
                      borderColor: 'gray-line.main',
                      px: 3,
                      py: 2,
                      bgcolor: 'primary.light',
                    }}
                  >
                    <AdxButton
                      variant={'outlined'}
                      onClick={handleClose}
                      sx={{ width: { xs: 1, md: '100px' } }}
                    >
                      Cancel
                    </AdxButton>
                    <AdxButton
                      isLoading={isLoading}
                      disabled={isLoading}
                      onClick={() => {
                        formikRef.current?.submitForm()
                      }}
                      sx={{ width: { xs: 1, md: '200px' } }}
                    >
                      {newCategory ? 'Create' : 'Save'}
                    </AdxButton>
                  </Box>
                </Box>
              </AdxFormModal>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default CategoryFormModal
